import { toaster } from '@/components/ui/toaster';
import { messagingSectionCountersQueryKey } from '@/hooks/queries/use-messaging-section-counters-query';
import { conversationQueryKeys } from '@/hooks/queries/conversation-query-keys';
import api from '@/libs/axiosInstance';
import type {
  ApiErrorResponse,
  ApiResponse,
  ReportCreatorHubParticipantRequest,
} from '@fluenceur/types';
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

type ConversationActionInput = { conversationId: string };
type ReportConversationInput = ConversationActionInput & ReportCreatorHubParticipantRequest;

const invalidateConversationModerationQueries = (
  queryClient: QueryClient,
  brandId: string,
  _conversationId: string
) => {
  queryClient.invalidateQueries({
    queryKey: conversationQueryKeys.all(brandId),
  });
  queryClient.invalidateQueries({ queryKey: messagingSectionCountersQueryKey(brandId) });
};

const getErrorDescription = (error: AxiosError<ApiErrorResponse>, t: TFunction) => {
  const errorCode = error.response?.data?.code;
  const translated = errorCode ? t(`errors.${errorCode}`, { defaultValue: '' }) : '';
  return translated || error.response?.data?.message || t('common.errors.unexpected');
};

export const useBlockConversation = (brandId: string) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation<void, AxiosError<ApiErrorResponse>, ConversationActionInput>({
    mutationFn: async ({ conversationId }) => {
      await api.post(`/dashboard/conversations/${conversationId}/block`, {});
    },
    onSuccess: (_, { conversationId }) => {
      invalidateConversationModerationQueries(queryClient, brandId, conversationId);
      toaster.success({ title: t('messages.moderation.block.success') });
    },
    onError: (error) => {
      toaster.error({
        title: t('messages.moderation.block.error'),
        description: getErrorDescription(error, t),
      });
    },
  });
};

export const useUnblockConversation = (brandId: string) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation<void, AxiosError<ApiErrorResponse>, ConversationActionInput>({
    mutationFn: async ({ conversationId }) => {
      await api.delete(`/dashboard/conversations/${conversationId}/block`);
    },
    onSuccess: (_, { conversationId }) => {
      invalidateConversationModerationQueries(queryClient, brandId, conversationId);
      toaster.success({ title: t('messages.moderation.unblock.success') });
    },
    onError: (error) => {
      toaster.error({
        title: t('messages.moderation.unblock.error'),
        description: getErrorDescription(error, t),
      });
    },
  });
};

export const useReportConversation = (brandId: string) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation<{ reportId: string }, AxiosError<ApiErrorResponse>, ReportConversationInput>({
    mutationFn: async ({ conversationId, ...report }) => {
      const response = await api.post<ApiResponse<{ reportId: string }>>(
        `/dashboard/conversations/${conversationId}/reports`,
        report
      );
      return response.data.data ?? { reportId: '' };
    },
    onSuccess: (_, { conversationId }) => {
      invalidateConversationModerationQueries(queryClient, brandId, conversationId);
      toaster.success({ title: t('messages.moderation.report.success') });
    },
    onError: (error) => {
      toaster.error({
        title: t('messages.moderation.report.error'),
        description: getErrorDescription(error, t),
      });
    },
  });
};
